import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useFocusEffect } from '@react-navigation/native';
import { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Dimensions, Easing, ScrollView, StyleSheet, View, } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Defs, LinearGradient as SvgLinearGradient, Rect, Stop, } from 'react-native-svg';
import styled from 'styled-components/native';
import { featureFlagAtom } from '@/atoms/Auth';
import { hasShownOutingItemsCollectedPopupAtom } from '@/atoms/User';
import MissionCompletedOverlay from '@/components/MissionCompletedOverlay/MissionCompletedOverlay';
import { ScreenLayout } from '@/components/ScreenLayout';
import { SccPressable } from '@/components/SccPressable';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { TutorialMissionTypeDto, } from '@/generated-sources/openapi';
import { useCompleteUserTutorialMission, useUserTutorialProgress, } from '@/hooks/useUserTutorialProgress';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { useCheckAuth } from '@/utils/checkAuth';
import ToastUtils from '@/utils/ToastUtils';
import HiddenMissionCollectedPopup from './components/HiddenMissionCollectedPopup';
import MissionCard from './components/MissionCard';
import MissionHero from './components/MissionHero';
import { TUTORIAL_MISSION_META, allMainMissionsCompletedIn, koreanOrdinal, mainMissionTypesOf, objectParticle, shouldExitTutorialMission, } from './constants';
function isMissionCompleted(mission) {
    return (mission === null || mission === void 0 ? void 0 : mission.completedAt) != null;
}
// 박원 figma 시안 (quest_card_dim) 의 완료 후 보조 링크. 미션 1/2 만 후속 화면이 있고
// 미션 3 은 후속 액션이 없어 링크 미노출. navigation 객체를 인자로 받아 onPress 클로저
// 안에서 navigate 호출.
function pickPostCompletionLink({ missionType, navigation, }) {
    switch (missionType) {
        case TutorialMissionTypeDto.RegisterInterestedRegionsAndThemes:
            return {
                text: '관심 지역, 관심 주제 수정하기 →',
                onPress: () => navigation.navigate('ProfileEditor'),
            };
        case TutorialMissionTypeDto.SavePlaceList:
            return {
                text: '저장리스트 다시 보기 →',
                // 단순 "다시 보기" 진입이므로 튜토리얼 완료 트리거(fromTutorial)는 주지 않는다.
                onPress: () => navigation.navigate('PublicPlaceLists', {}),
            };
        case TutorialMissionTypeDto.UpvoteAccessibility:
            return undefined;
        // 후속 링크 없음 (박원 시안 카드1의 "관심 지역, 관심 주제 수정하기 →" 는 미션2
        // 텍스트 복붙 흔적이며 기획에서 "링크 없음" 으로 확정됐다).
        case TutorialMissionTypeDto.ViewTutorialImages:
            return undefined;
        case TutorialMissionTypeDto.HiddenAppSurvey:
            return undefined;
        default: {
            const _exhaustive = missionType;
            return _exhaustive;
        }
    }
}
// 박원 2026-05-27 시안 stage → bubble variant + float 매핑.
// 진행 중 말풍선은 "완료 개수"가 아니라 **현재 미션 타입**에서 파생한다 — 가입 시점에
// 따라 미션 순서가 다르므로(v1/v2) 개수로는 어떤 외출템을 찾는 중인지 알 수 없다.
// v1 셋(REGISTER→SAVE→UPVOTE)에서는 결과가 기존 개수 기반 매핑과 완전히 동일하다.
function pickBubbleState({ currentMissionType, allMainCompleted, hasShownOutingItemsCollectedPopup, isHiddenCompleted, }) {
    if (isHiddenCompleted) {
        return { variant: 'v6_all_complete', float: false };
    }
    if (allMainCompleted) {
        if (!hasShownOutingItemsCollectedPopup) {
            return { variant: 'v4_all_items_collected', float: false };
        }
        return { variant: 'v5_seek_hidden', float: true };
    }
    // ponytail: 메인 미션이 하나도 없으면 allMainCompleted 가 true 라 여기 도달 불가.
    // 그래도 문구를 날조하지 않고 말풍선 미노출(null)로 떨어뜨린다.
    if (!currentMissionType) {
        return { variant: null, float: false };
    }
    return {
        variant: TUTORIAL_MISSION_META[currentMissionType].bubbleVariant,
        float: true,
    };
}
const SCREEN_WIDTH = Dimensions.get('window').width;
export default function TutorialMissionScreen({ navigation, route, }) {
    var _a;
    const checkAuth = useCheckAuth();
    const featureFlags = useAtomValue(featureFlagAtom);
    const { data: progress, refetch } = useUserTutorialProgress();
    const completeMission = useCompleteUserTutorialMission();
    const insets = useSafeAreaInsets();
    const scrollRef = useRef(null);
    // ScrollView 내부 content 컨테이너 ref. 카드 y 좌표 측정의 기준 좌표계.
    // New Architecture(Fabric)에서는 getInnerViewNode() node handle 기반 measureLayout
    // 이 동작하지 않으므로, 직접 content View 를 감싸고 그 host instance ref 를 기준으로
    // measureLayout 한다.
    const scrollContentRef = useRef(null);
    // 각 메인 미션 카드 wrapper 의 ref. hero 의 외출템 탭 시 해당 카드로 스크롤한다.
    const cardRefs = useRef([]);
    const scrollToMissionCard = useCallback((index) => {
        const cardNode = cardRefs.current[index];
        const contentNode = scrollContentRef.current;
        if (!cardNode || !contentNode) {
            return;
        }
        cardNode.measureLayout(contentNode, (_x, y) => {
            var _a;
            // 카드가 화면 최상단에 딱 붙지 않도록 약간의 여백을 두고 스크롤.
            (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ y: Math.max(0, y - 16), animated: true });
        }, () => { });
    }, []);
    // 미션 완료 후 popTo 로 돌아왔을 때 instant scroll top.
    // 일반 back 으로 돌아온 경우엔 token 이 변하지 않으므로 스크롤이 유지된다.
    const scrollResetToken = (_a = route.params) === null || _a === void 0 ? void 0 : _a.scrollResetToken;
    useEffect(() => {
        var _a;
        if (scrollResetToken == null) {
            return;
        }
        (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ y: 0, animated: false });
    }, [scrollResetToken]);
    // 튜토리얼 미대상 사용자가 deeplink 등으로 우회 진입한 경우 broken UX(빈 화면) 노출 방지.
    // 판정은 shouldExitTutorialMission 이 전담한다 — flag 신호만 보면 익명 유저는
    // featureFlags 가 영원히 null 이라 영구히 빈 화면에 갇힌다.
    useEffect(() => {
        if (!shouldExitTutorialMission(featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.enabledFlags.has('USER_TUTORIAL'), progress === null || progress === void 0 ? void 0 : progress.missions)) {
            return;
        }
        // 딥링크 콜드스타트로 스택에 이전 화면이 없으면 goBack() 이 no-op 이라 여전히 갇힌다.
        // RootScreen.runWebRouteGate 와 동일한 패턴으로 홈으로 보낸다.
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.navigate('Main');
        }
    }, [featureFlags, progress === null || progress === void 0 ? void 0 : progress.missions, navigation]);
    const [showHiddenCollected, setShowHiddenCollected] = useState(false);
    const [hasShownOutingItemsCollectedPopup, setHasShownOutingItemsCollectedPopup,] = useAtom(hasShownOutingItemsCollectedPopupAtom);
    const [showOutingItemsCollected, setShowOutingItemsCollected] = useState(false);
    useFocusEffect(useCallback(() => {
        refetch();
    }, [refetch]));
    const missionByType = useMemo(() => {
        const map = new Map();
        progress === null || progress === void 0 ? void 0 : progress.missions.forEach(m => map.set(m.missionType, m));
        return map;
    }, [progress]);
    // 미션 순서/구성은 서버 응답 배열 순서가 유일한 정답이다 (가입 시점에 따라 v1/v2
    // 서로 다른 셋이 내려온다). 앱은 순서를 하드코딩하지 않는다.
    const mainMissionTypes = useMemo(() => { var _a; return mainMissionTypesOf((_a = progress === null || progress === void 0 ? void 0 : progress.missions) !== null && _a !== void 0 ? _a : []); }, [progress === null || progress === void 0 ? void 0 : progress.missions]);
    const collectedMainCount = useMemo(() => {
        return mainMissionTypes.filter(type => isMissionCompleted(missionByType.get(type))).length;
    }, [mainMissionTypes, missionByType]);
    // 빈 미션 배열을 "전부 완료"로 오판하면 외출템 수집 완료 팝업/히든 CTA/모자 hot zone
    // 이 헛발사된다 → 판정은 빈 배열 가드가 든 공용 헬퍼로만 한다.
    const allMainCompleted = useMemo(() => { var _a; return allMainMissionsCompletedIn((_a = progress === null || progress === void 0 ? void 0 : progress.missions) !== null && _a !== void 0 ? _a : []); }, [progress === null || progress === void 0 ? void 0 : progress.missions]);
    // 서버 순서상 첫 미완료 메인 미션 (= 지금 깰 수 있는 미션).
    const currentMainMissionType = useMemo(() => mainMissionTypes.find(type => !isMissionCompleted(missionByType.get(type))), [mainMissionTypes, missionByType]);
    const isHiddenCompleted = isMissionCompleted(missionByType.get(TutorialMissionTypeDto.HiddenAppSurvey));
    const tryCompleteHiddenMission = useCallback(() => {
        if (!allMainCompleted || isHiddenCompleted) {
            return;
        }
        if (completeMission.isPending) {
            return;
        }
        completeMission.mutate({ missionType: TutorialMissionTypeDto.HiddenAppSurvey }, {
            onSuccess: () => {
                setShowHiddenCollected(true);
            },
            onError: error => {
                var _a;
                if (error instanceof AxiosError && ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 400) {
                    ToastUtils.show('설문 제출이 확인되지 않았어요.\n잠시 후 다시 시도하거나 설문을 다시 제출해주세요.');
                }
            },
        });
    }, [allMainCompleted, isHiddenCompleted, completeMission]);
    // tally form Webview 진입 후 화면 복귀 시 hidden 완료 API 자동 호출.
    // 서버가 tally API로 직접 검증 (idempotent + 미제출 시 400 silent) 하므로 안전하게 시도.
    const wasTallyOpenedRef = useRef(false);
    useFocusEffect(useCallback(() => {
        if (!wasTallyOpenedRef.current) {
            return;
        }
        wasTallyOpenedRef.current = false;
        tryCompleteHiddenMission();
    }, [tryCompleteHiddenMission]));
    // 모든 메인 미션 완료 시 "외출템 수집 완료!" 팝업 1회 노출.
    // useFocusEffect 로 감싸서 TutorialMissionScreen 이 focus 됐을 때만 trigger.
    // (미션 3 화면 같은 자식 스크린이 push 된 상태에서는 trigger 되지 않도록 — 자식
    // 화면 위에 외출템 팝업이 뜨는 걸 막는다.)
    useFocusEffect(useCallback(() => {
        if (!allMainCompleted) {
            return;
        }
        if (hasShownOutingItemsCollectedPopup) {
            return;
        }
        setShowOutingItemsCollected(true);
    }, [allMainCompleted, hasShownOutingItemsCollectedPopup]));
    const handleStartMission = useCallback((missionType) => {
        const meta = TUTORIAL_MISSION_META[missionType];
        checkAuth(() => {
            if (meta.navigateTo === 'TallyForm') {
                return;
            }
            if (meta.navigateTo === 'BasicUsageTutorial') {
                // 계뿌클 기본 사용법 3장 화면. 마지막 장 CTA 가 미션 완료 API 를 호출한다.
                navigation.navigate('BasicUsageTutorial');
            }
            else if (meta.navigateTo === 'InterestedRegionAndThemes') {
                navigation.navigate('InterestedRegionAndThemes', {});
            }
            else if (meta.navigateTo === 'PublicPlaceLists') {
                // 튜토리얼 미션 2 (SAVE_PLACE_LIST) 진입은 전용 라우트로. 라우트 이름이
                // 컨텍스트를 명시 — 자식 PlaceListDetail 이 fromTutorial 을 인식해서
                // 미션 완료 mutation 을 명시 호출한다 (일반 PublicPlaceLists 진입은 X).
                navigation.navigate('TutorialMissionSavePlaceList', {
                    fromTutorial: true,
                });
            }
            else if (meta.navigateTo === 'TutorialUpvoteAccessibilityMission') {
                navigation.navigate('TutorialUpvoteAccessibilityMission');
            }
            else if (meta.navigateTo === 'Main') {
                navigation.navigate('Main');
            }
        });
    }, [checkAuth, navigation]);
    const handleHiddenMissionPress = useCallback(() => {
        if (!allMainCompleted) {
            return;
        }
        if (isHiddenCompleted) {
            return;
        }
        checkAuth(() => {
            const url = progress === null || progress === void 0 ? void 0 : progress.hiddenMissionTallyFormUrl;
            if (!url) {
                return;
            }
            wasTallyOpenedRef.current = true;
            navigation.navigate('Webview', {
                url,
                fixedTitle: '숨겨진 외출템 모아보기',
                headerVariant: 'appbar',
                confirmOnClose: false,
            });
        });
    }, [allMainCompleted, isHiddenCompleted, checkAuth, progress, navigation]);
    // hero 의 외출템(?) hot zone 탭 핸들러.
    // - 현재 진행 가능 미션 (= 다음에 깰 미션 = 첫 미완료 + 이전 모두 완료): 해당 미션
    //   list item 으로 스크롤 (미션 시작 페이지로 바로 보내지 않는다).
    // - 이미 완료된 외출템: 무반응.
    // - 잠긴 외출템(이전 미션 미완료): 무반응.
    const handleMissionItemPress = useCallback((missionType) => {
        if (missionType !== currentMainMissionType) {
            return;
        }
        scrollToMissionCard(mainMissionTypes.indexOf(missionType));
    }, [currentMainMissionType, mainMissionTypes, scrollToMissionCard]);
    // 박원 2026-05-27 시안. hero PNG 자체는 정지하고 hero 위에 overlay 한 말풍선만
    // 둥실 애니메이션을 적용한다. variant 별 텍스트/위치는 SpeechBubble 이 처리.
    // - variant 1/2/3: 미션 진행 중 (mainCount 0/1/2) → 둥실
    // - variant 4: 외출템 다 모았어 (allMain, !popupShown) → 정지
    // - variant 5: 숨겨진 외출템도 모아볼까 (allMain, popupShown, !hidden) → 둥실
    // - variant 6: 다모았다 (hidden 완료) → 정지
    const bubbleState = pickBubbleState({
        currentMissionType: currentMainMissionType,
        allMainCompleted,
        hasShownOutingItemsCollectedPopup,
        isHiddenCompleted,
    });
    // 외출템 다 모은 뒤(variant 5, 6) 노출되는 sticky bottom CTA + tooltip (박원 시안).
    // - 버튼: 화면 하단 50px 위 고정 (figma 2004:14517 / 2004:15337).
    // - tooltip: 버튼 위 4↔10px 상하 둥실 (figma 2004:14518 / 2004:15338).
    //   히든 완료 variant 6 frame metadata 에도 tooltip 노드가 그대로 있으므로 같이 노출.
    const showStickyHiddenCta = allMainCompleted;
    const showHiddenTooltip = allMainCompleted;
    const tooltipFloatY = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        if (!showHiddenTooltip) {
            tooltipFloatY.stopAnimation();
            tooltipFloatY.setValue(0);
            return;
        }
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(tooltipFloatY, {
                toValue: -6,
                duration: 1000,
                easing: Easing.inOut(Easing.ease),
                useNativeDriver: true,
            }),
            Animated.timing(tooltipFloatY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.inOut(Easing.ease),
                useNativeDriver: true,
            }),
        ]));
        loop.start();
        return () => loop.stop();
    }, [showHiddenTooltip, tooltipFloatY]);
    const handleHiddenListPress = useCallback(() => {
        if (!allMainCompleted) {
            return;
        }
        checkAuth(() => {
            // 보상(히든) 저장리스트 id 가 있으면 해당 place list 상세로 deeplink 진입.
            // 서버에 보상 리스트가 설정되지 않은 경우(null)엔 기존처럼 공개 리스트 목록으로.
            const rewardPlaceListId = progress === null || progress === void 0 ? void 0 : progress.rewardPlaceListId;
            if (rewardPlaceListId) {
                navigation.navigate('PlaceListDetail', {
                    placeListId: rewardPlaceListId,
                });
                return;
            }
            navigation.navigate('PublicPlaceLists', { fromTutorial: true });
        });
    }, [allMainCompleted, checkAuth, navigation, progress]);
    // 외출템 다 모은 직후(variant 4) → 팝업 본 뒤(variant 5) 전환 시 hero PNG 를
    // "Click!" 인디케이터가 있는 stage3_seek_hidden.png 로 swap.
    // 서버는 단일 stage3 URL 만 보내므로 클라이언트가 같은 prefix 의 다른 파일명으로
    // URL 을 string-replace. PROD/DEV 양쪽 모두 같은 패턴이라 안전.
    const effectiveHeroImageUrl = useMemo(() => {
        var _a;
        const url = (_a = progress === null || progress === void 0 ? void 0 : progress.heroImageUrl) !== null && _a !== void 0 ? _a : '';
        if (!url)
            return url;
        if (allMainCompleted &&
            hasShownOutingItemsCollectedPopup &&
            !isHiddenCompleted) {
            return url.replace(/stage3\.png(\?|$)/, 'stage3_seek_hidden.png$1');
        }
        return url;
    }, [
        progress === null || progress === void 0 ? void 0 : progress.heroImageUrl,
        allMainCompleted,
        hasShownOutingItemsCollectedPopup,
        isHiddenCompleted,
    ]);
    // 메인 미션이 없으면(USER_TUTORIAL flag 미대상/익명 → 서버가 missions: [])
    // 그릴 카드도 없고 "0개의 미션을 완료해봐요!" 같은 문구만 남는다. 위 이탈 가드
    // effect 가 goBack/홈 이동을 하는 사이 한 프레임만 빈 화면을 보여준다.
    if (!progress || mainMissionTypes.length === 0) {
        return _jsx(ScreenLayout, { isHeaderVisible: true });
    }
    return (_jsx(ScreenLayout, { isHeaderVisible: true, children: _jsx(LogParamsProvider, { params: { displaySectionName: 'tutorial_mission_screen' }, children: _jsxs(BgContainer, { children: [_jsx(ScrollView, { ref: scrollRef, showsVerticalScrollIndicator: false, children: _jsxs(View, { ref: scrollContentRef, collapsable: false, children: [_jsx(MissionHero, { hiddenActive: allMainCompleted, hiddenCompleted: isHiddenCompleted, onHiddenPress: handleHiddenMissionPress, imageWidth: SCREEN_WIDTH, heroImageUrl: effectiveHeroImageUrl, bubbleVariant: bubbleState.variant, bubbleFloat: bubbleState.float, mainMissionTypes: mainMissionTypes, onMissionItemPress: handleMissionItemPress, onBubblePress: 
                                    // 진행 중 미션이 있을 때(= 메인 미션 미완료, bubble variant 1/2/3)만
                                    // 말풍선 탭 → 현재 진행 미션 카드로 스크롤. 다 모은 뒤(v4/5/6)엔 미전달.
                                    allMainCompleted
                                        ? undefined
                                        : () => scrollToMissionCard(collectedMainCount) }), _jsxs(ContentArea, { children: [_jsx(SectionTitle, { children: `윌리와 함께\n${mainMissionTypes.length}개의 미션을 완료해봐요!` }), _jsx(CardsWrapper, { children: mainMissionTypes.map((missionType, index) => {
                                                const mission = missionByType.get(missionType);
                                                const meta = TUTORIAL_MISSION_META[missionType];
                                                const isCompleted = isMissionCompleted(mission);
                                                const previousMissions = mainMissionTypes.slice(0, index);
                                                const isPreviousCompleted = previousMissions.every(prev => isMissionCompleted(missionByType.get(prev)));
                                                const isDimmed = !isPreviousCompleted && !isCompleted;
                                                const isCurrent = isPreviousCompleted && !isCompleted;
                                                const postCompletionLink = pickPostCompletionLink({
                                                    missionType,
                                                    navigation,
                                                });
                                                return (_jsx(View, { ref: el => {
                                                        cardRefs.current[index] = el;
                                                    }, collapsable: false, children: _jsx(MissionCard, { missionType: missionType, missionIndex: index + 1, meta: meta, isCompleted: isCompleted, isCurrent: isCurrent, isDimmed: isDimmed, dimText: index > 0
                                                            ? `외출템 ${index}${objectParticle(index)} 모으면, ${koreanOrdinal(index + 1)} 미션이 열려요!`
                                                            : undefined, onStart: () => handleStartMission(missionType), postCompletionLinkText: postCompletionLink === null || postCompletionLink === void 0 ? void 0 : postCompletionLink.text, onPostCompletionLinkPress: postCompletionLink === null || postCompletionLink === void 0 ? void 0 : postCompletionLink.onPress }) }, missionType));
                                            }) }), _jsx(View, { style: { height: showStickyHiddenCta ? 140 : 40 } })] })] }) }), showStickyHiddenCta && (_jsxs(StickyHiddenCtaWrapper, { pointerEvents: "box-none", style: { bottom: insets.bottom + 20 }, children: [showHiddenTooltip && (_jsxs(Animated.View, { style: {
                                    alignItems: 'center',
                                    marginBottom: 4,
                                    transform: [{ translateY: tooltipFloatY }],
                                    // figma 2004:14518 tooltip drop-shadow 2px 4px 2px rgba(0,0,0,0.25).
                                    shadowColor: '#000',
                                    shadowOffset: { width: 2, height: 4 },
                                    shadowOpacity: 0.25,
                                    shadowRadius: 2,
                                    elevation: 3,
                                }, children: [_jsx(TooltipBubble, { children: _jsx(TooltipText, { children: "\uD788\uB4E0 \uB9AC\uC2A4\uD2B8\uB97C \uD655\uC778\uD574\uBCF4\uC138\uC694!" }) }), _jsx(TooltipArrow, {})] })), _jsx(HiddenCtaShadow, { children: _jsxs(HiddenCtaButton, { elementName: "tutorial_mission_hidden_list_sticky_cta", onPress: handleHiddenListPress, children: [_jsxs(Svg, { style: StyleSheet.absoluteFillObject, preserveAspectRatio: "none", viewBox: "0 0 1 1", children: [_jsx(Defs, { children: _jsxs(SvgLinearGradient, { id: "hiddenCtaGrad", x1: "0", y1: "0", x2: "0", y2: "1", children: [_jsx(Stop, { offset: "0.111", stopColor: "#0C76F7" }), _jsx(Stop, { offset: "1", stopColor: "#0A5EC6" })] }) }), _jsx(Rect, { width: "1", height: "1", fill: "url(#hiddenCtaGrad)" })] }), _jsx(HiddenCtaText, { children: "\uACC4\uBFCC\uD074 \uD788\uB4E0 \uB9DB\uC9D1 \uB9AC\uC2A4\uD2B8 \uBCF4\uAE30!" })] }) })] })), showOutingItemsCollected && (_jsx(MissionCompletedOverlay, { isVisible: true, variant: "outing-items", itemImage: require('@/assets/img/tutorial/mission_complete_img_outing_items.png'), description: '윌리의 외출템을 모두 모았어요!\n이제 계뿌클 히든 맛집 리스트를\n확인할 수 있어요', confirmElementName: "tutorial_outing_items_collected_confirm", onClose: () => {
                            setShowOutingItemsCollected(false);
                            setHasShownOutingItemsCollectedPopup(true);
                        } })), showHiddenCollected && (_jsx(HiddenMissionCollectedPopup, { isVisible: true, onClose: () => setShowHiddenCollected(false) }))] }) }) }));
}
const BgContainer = styled.View `
  flex: 1;
  background-color: ${color.white};
`;
const ContentArea = styled.View `
  background-color: #e9f4c3;
  padding: 32px 16px 40px;
`;
const SectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  color: ${color.black};
  text-align: center;
  margin-bottom: 32px;
`;
const CardsWrapper = styled.View `
  gap: 8px;
`;
// Figma 2004:14517 / 2004:15337: 화면 하단 50px 위에 고정된 button container.
// pointerEvents=box-none 으로 wrapper 자체는 터치 통과, 내부 버튼/툴팁만 터치 수신.
const StickyHiddenCtaWrapper = styled.View `
  position: absolute;
  left: 16px;
  right: 16px;
  align-items: center;
`;
// Figma 2004:15337: 358x56, 그라데이션(#0C76F7→#074591), 흰색 1.5px border,
// box-shadow 2px 4px 4px rgba(0,0,0,0.25). RN 의 shadow 는 overflow:hidden 와
// 같이 쓰면 잘리므로 wrapper(Shadow) + inner(border + overflow + svg) 두 단으로
// 분리.
const HiddenCtaShadow = styled.View `
  width: 100%;
  border-radius: 8px;
  /* iOS */
  shadow-color: #000;
  shadow-offset: 2px 4px;
  shadow-opacity: 0.25;
  shadow-radius: 4px;
  /* Android */
  elevation: 6;
`;
const HiddenCtaButton = styled(SccPressable) `
  width: 100%;
  height: 56px;
  border-radius: 8px;
  border-width: 1.5px;
  border-color: ${color.white};
  background-color: #0c76f7;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const HiddenCtaText = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  color: ${color.white};
`;
// Figma 2004:14518 tooltip: 157x34 (text 28 + arrow 6), 진한 회색 배경 + 흰 텍스트.
const TooltipBubble = styled.View `
  background-color: ${color.gray90v2};
  border-radius: 8px;
  padding: 6px 18px;
  align-items: center;
  justify-content: center;
`;
const TooltipText = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.26px;
  color: ${color.white};
`;
// 말풍선 ↘ 아래로 향하는 작은 삼각 화살표. 10x6 (figma 28:8534 기준).
const TooltipArrow = styled.View `
  width: 0;
  height: 0;
  border-left-width: 5px;
  border-right-width: 5px;
  border-top-width: 6px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: ${color.gray90v2};
`;
